import { FunctionComponent, useState } from "react";
import Link from "next/link";
import { useTranslation } from "next-i18next/pages";
import styles from "../../styles/home/CaseStudyHighlight.module.css";

interface CaseFact {
  value: string;
  label: string;
}

interface CaseSlide {
  id: string;
  industry: string;
  title: string;
  paragraphs: string[];
  quote?: string;
  quoteAuthor?: string;
  facts: CaseFact[];
}

interface CaseStudyHighlightProps {}

const CaseStudyHighlight: FunctionComponent<CaseStudyHighlightProps> = () => {
  const { t } = useTranslation("common");
  const slides: CaseSlide[] = t("homeModern.caseHighlight.slides", {
    returnObjects: true
  }) as CaseSlide[];
  const [activeIndex, setActiveIndex] = useState(0);
  const slide = slides[activeIndex];

  const showPrevious = () =>
    setActiveIndex((activeIndex - 1 + slides.length) % slides.length);
  const showNext = () => setActiveIndex((activeIndex + 1) % slides.length);

  return (
    <section id="case-study" className={styles.section}>
      <div className={styles.caseVisual} aria-hidden="true">
        <div className={styles.ring} />
      </div>
      <div className={styles.panel}>
        <div key={slide.id} className={styles.slide}>
          <div className={styles.textColumn}>
            <div className={styles.kicker}>
              {t("homeModern.caseHighlight.kicker")} · {slide.industry}
            </div>
            <h2 className={styles.title}>{slide.title}</h2>

            {slide.paragraphs.map((paragraph: string) => (
              <p key={paragraph} className={styles.paragraph}>
                {paragraph}
              </p>
            ))}

            <Link href={`/case-studies/${slide.id}`} className={styles.cta}>
              {t("homeModern.caseHighlight.cta")} <span aria-hidden="true">→</span>
            </Link>
          </div>

          <div className={styles.asideColumn}>
            {slide.quote && (
              <blockquote className={styles.quote}>
                <p>{slide.quote}</p>
                <footer className={styles.quoteAuthor}>
                  {slide.quoteAuthor}
                </footer>
              </blockquote>
            )}

            <div className={styles.factGrid}>
              {slide.facts.map((fact: CaseFact) => (
                <div key={fact.label} className={styles.factCard}>
                  <div className={styles.factValue}>{fact.value}</div>
                  <div className={styles.factLabel}>{fact.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.controls}>
          <button
            type="button"
            className={styles.arrowButton}
            onClick={showPrevious}
            aria-label={t("homeModern.caseHighlight.prevLabel")}
          >
            ←
          </button>

          <div className={styles.dots}>
            {slides.map((dotSlide: CaseSlide, index: number) => (
              <button
                key={dotSlide.id}
                type="button"
                className={
                  index === activeIndex
                    ? `${styles.dot} ${styles.dotActive}`
                    : styles.dot
                }
                onClick={() => setActiveIndex(index)}
                aria-label={dotSlide.title}
                aria-current={index === activeIndex}
              />
            ))}
          </div>

          <button
            type="button"
            className={styles.arrowButton}
            onClick={showNext}
            aria-label={t("homeModern.caseHighlight.nextLabel")}
          >
            →
          </button>
        </div>
      </div>
    </section>
  );
};

export default CaseStudyHighlight;
